import { hasTelegramLaunchContext } from "./telegramContext";

export type TelegramColorScheme = "light" | "dark";
export type ThemeChangeHandler = (colorScheme: TelegramColorScheme) => void;

export interface TelegramAdapter {
  isTelegram: boolean;
  isPreview: boolean;
  initData: string;
  readonly startParam: string | null;
  readonly colorScheme: TelegramColorScheme;
  readonly themeParams: Readonly<Record<string, string>>;
  ready(): void;
  onThemeChange(handler: ThemeChangeHandler): () => void;
  setBack(onBack: (() => void) | null): void;
  hapticLight(): void;
  hapticSuccess(): void;
  requestWriteAccess(): Promise<boolean>;
  share(url: string): void;
}

const telegram = window.Telegram?.WebApp;

// Loading Telegram's Web App SDK in an ordinary browser can create an empty
// WebApp object. Only signed launch data denotes a real Telegram context.
const telegramContext = hasTelegramLaunchContext(telegram?.initData);
let activeBackHandler: (() => void) | null = null;
let currentColorScheme: TelegramColorScheme = "light";
let currentThemeParams: Record<string, string> = {};
const themeHandlers = new Set<ThemeChangeHandler>();

const fallbackThemeParams: Record<TelegramColorScheme, Record<string, string>> = {
  light: {
    bg_color: "#f7f8fc",
    secondary_bg_color: "#ffffff",
    text_color: "#17212e",
    hint_color: "#7d8797",
    link_color: "#5b4de8",
    button_color: "#5b4de8",
    button_text_color: "#ffffff",
    header_bg_color: "#f7f8fc",
    bottom_bar_bg_color: "#ffffff",
    accent_text_color: "#5b4de8",
    section_bg_color: "#ffffff",
    section_header_text_color: "#657083",
    section_separator_color: "#e7eaf0",
    subtitle_text_color: "#7d8797",
    destructive_text_color: "#a93631",
  },
  dark: {
    bg_color: "#0f172a",
    secondary_bg_color: "#182235",
    text_color: "#f1f5f9",
    hint_color: "#9aa8ba",
    link_color: "#9a91ff",
    button_color: "#766bef",
    button_text_color: "#ffffff",
    header_bg_color: "#0f172a",
    bottom_bar_bg_color: "#182235",
    accent_text_color: "#b0a9ff",
    section_bg_color: "#182235",
    section_header_text_color: "#aab5c4",
    section_separator_color: "#29364b",
    subtitle_text_color: "#9aa8ba",
    destructive_text_color: "#ff8e87",
  },
};

function applyTheme(colorScheme: TelegramColorScheme, themeParams: Record<string, string> = {}): void {
  currentColorScheme = colorScheme;
  currentThemeParams = { ...fallbackThemeParams[colorScheme], ...themeParams };
  const root = document.documentElement;
  root.dataset.tgColorScheme = colorScheme;
  root.style.colorScheme = colorScheme;
  root.style.setProperty("--tg-color-scheme", colorScheme);

  for (const [key, value] of Object.entries(currentThemeParams)) {
    root.style.setProperty(`--tg-theme-${key.replaceAll("_", "-")}`, value);
  }

  const themeColor = document.querySelector('meta[name="theme-color"]');
  themeColor?.setAttribute("content", currentThemeParams.bg_color);
  for (const handler of themeHandlers) handler(colorScheme);
}

function syncTelegramChrome(): void {
  // These keywords delegate the actual colors to Telegram's current theme.
  telegram?.setHeaderColor?.("bg_color");
  telegram?.setBackgroundColor?.("bg_color");
  telegram?.setBottomBarColor?.("bottom_bar_bg_color");
}

function syncTelegramViewport(): void {
  if (!telegramContext || !telegram) return;
  const root = document.documentElement;
  if (typeof telegram.viewportHeight === "number") {
    root.style.setProperty("--tg-viewport-height", `${telegram.viewportHeight}px`);
  }
  if (typeof telegram.viewportStableHeight === "number") {
    root.style.setProperty("--tg-viewport-stable-height", `${telegram.viewportStableHeight}px`);
  }
  for (const [prefix, insets] of [
    ["--tg-safe-area-inset-", telegram.safeAreaInset],
    ["--tg-content-safe-area-inset-", telegram.contentSafeAreaInset],
  ] as const) {
    for (const edge of ["top", "bottom", "left", "right"] as const) {
      const value = insets?.[edge];
      if (typeof value === "number") root.style.setProperty(`${prefix}${edge}`, `${value}px`);
    }
  }
}

function handleTelegramThemeChanged(): void {
  const colorScheme = telegram?.colorScheme === "dark" ? "dark" : "light";
  applyTheme(colorScheme, telegram?.themeParams ?? {});
  syncTelegramChrome();
}

function handleTelegramViewportChanged(): void {
  syncTelegramViewport();
}

function handleTelegramSafeAreaChanged(): void {
  syncTelegramViewport();
}

function handlePreviewThemeChanged(event?: MediaQueryListEvent): void {
  const colorScheme = event?.matches || previewMediaQuery?.matches ? "dark" : "light";
  applyTheme(colorScheme);
}

const chatStartParamPattern = /^chat_([0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

export function parseChatStartParam(value: string | null | undefined): string | null {
  const match = value?.match(chatStartParamPattern);
  if (!match) return null;
  const compact = match[1].replaceAll("-", "").toLowerCase();
  return `${compact.slice(0, 8)}-${compact.slice(8, 12)}-${compact.slice(12, 16)}-${compact.slice(16, 20)}-${compact.slice(20)}`;
}

const previewMediaQuery = !telegramContext ? window.matchMedia?.("(prefers-color-scheme: dark)") : undefined;
if (telegramContext && telegram) {
  handleTelegramThemeChanged();
  syncTelegramViewport();
  telegram.onEvent?.("themeChanged", handleTelegramThemeChanged);
  telegram.onEvent?.("viewportChanged", handleTelegramViewportChanged);
  telegram.onEvent?.("safeAreaChanged", handleTelegramSafeAreaChanged);
  telegram.onEvent?.("contentSafeAreaChanged", handleTelegramSafeAreaChanged);
} else {
  handlePreviewThemeChanged();
  previewMediaQuery?.addEventListener?.("change", handlePreviewThemeChanged);
}

export const telegramAdapter: TelegramAdapter = {
  isTelegram: telegramContext,
  // A query parameter may enable local browser preview, never bypass auth in Telegram.
  isPreview: !telegramContext,
  initData: telegram?.initData ?? "",
  get startParam() {
    const startParam = new URLSearchParams(telegram?.initData ?? "").get("start_param");
    return telegramContext && startParam ? startParam : null;
  },
  get colorScheme() {
    return currentColorScheme;
  },
  get themeParams() {
    return currentThemeParams;
  },
  ready() {
    telegram?.ready?.();
    telegram?.expand?.();
    syncTelegramViewport();
    syncTelegramChrome();
  },
  onThemeChange(handler) {
    themeHandlers.add(handler);
    handler(currentColorScheme);
    return () => themeHandlers.delete(handler);
  },
  setBack(onBack) {
    if (!telegram?.BackButton) return;
    if (activeBackHandler) telegram.BackButton.offClick?.(activeBackHandler);
    activeBackHandler = onBack;
    if (onBack) {
      telegram.BackButton.onClick?.(onBack);
      telegram.BackButton.show?.();
    } else {
      telegram.BackButton.hide?.();
    }
  },
  hapticLight() {
    try { window.Telegram?.WebApp?.HapticFeedback?.impactOccurred?.("light"); } catch { /* optional */ }
  },
  hapticSuccess() {
    try { window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.("success"); } catch { /* optional */ }
  },
  requestWriteAccess() {
    const request = telegram?.requestWriteAccess;
    const onEvent = telegram?.onEvent;
    const offEvent = telegram?.offEvent;
    if (!telegramContext || !request || !onEvent || !offEvent) {
      return Promise.resolve(false);
    }
    return new Promise((resolve) => {
      let settled = false;
      let timeout: number | null = null;
      const finish = (granted: boolean) => {
        if (settled) return;
        settled = true;
        offEvent("writeAccessRequested", onResult);
        if (timeout !== null) window.clearTimeout(timeout);
        resolve(granted);
      };
      const onResult = (event?: { status?: "allowed" | "cancelled" }) => finish(event?.status === "allowed");
      onEvent("writeAccessRequested", onResult);
      timeout = window.setTimeout(() => finish(false), 60_000);
      try {
        request();
      } catch {
        finish(false);
      }
    });
  },
  share(url) {
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(url)}`;
    if (telegram?.openTelegramLink) telegram.openTelegramLink(shareUrl);
    else window.open(shareUrl, "_blank", "noopener,noreferrer");
  },
};
